import { Modal as BaseModal } from "@mui/base/Modal";
import clsx from "clsx";
import { forwardRef } from "react";

import { Icon } from "~/assets/icons/Icon";
import labels from "~/helpers/labels";

import Button from "../button/Button";
import styles from "./Modal.module.scss";

const Backdrop = forwardRef<
  HTMLDivElement,
  { open?: boolean; className: string; ownerState?: unknown }
>((props, ref) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { open, className, ownerState, ...other } = props;

  return (
    <div
      className={clsx(
        styles.backdrop,
        { "base-Backdrop-open": open },
        className,
      )}
      ref={ref}
      {...other}
    />
  );
});

type ModalSize = "sm" | "md" | "lg" | "xl";

type ModalProps = {
  isOpen: boolean;
  modalLabel: JSX.Element;
  closeModal: () => void;
  children: React.ReactNode;
  isDisabled?: boolean;
  buttonLabel?: string;
  buttonType?: string;
  formId?: string;
  size?: ModalSize;
  width?: string;
  height?: string;
  action?: () => void;
  optionalButtonLabel?: string | null;
  actionButtonColor?: string;
  optionalButtonAction?: () => void;
  isLoading?: boolean;
  isLoadingOptionalButton?: boolean;
  /**
   * Hide the default Cancel button in the footer. Use only for terminal
   * steps where dismissing is semantically not a "cancel" (e.g. MFA backup
   * codes — the operation has already succeeded server-side and cannot be
   * undone; the X / ESC remain available for plain dismissal).
   */
  hideCancel?: boolean;
  /**
   * Disable every dismissal path: hides the header X, blocks the backdrop
   * click, and ignores ESC. Use only when the user MUST interact with the
   * primary action to continue (e.g. acknowledging unrecoverable MFA backup
   * codes). The action button remains the sole exit.
   */
  disableDismiss?: boolean;
  onTransitionExited?: () => void;
  footerLeft?: React.ReactNode;
  /** @deprecated this is only supposed to be used for the Stripe modal,
   * any other modals should have dynamic background color that depends on the theme */
  forceWhiteBackground?: boolean;
};

const SIZE_WIDTH: Record<ModalSize, string> = {
  sm: "28rem", // ~448px
  md: "34rem", // default
  lg: "48rem", // ~768px
  xl: "64rem", // ~1024px
};

const Modal = ({
  isOpen,
  closeModal,
  children,
  isDisabled,
  buttonLabel,
  formId,
  modalLabel,
  size = "md",
  width,
  height,
  action,
  optionalButtonLabel,
  actionButtonColor,
  optionalButtonAction,
  isLoading,
  isLoadingOptionalButton,
  onTransitionExited,
  footerLeft,
  forceWhiteBackground,
  hideCancel,
  disableDismiss,
}: ModalProps) => {
  const computedWidth = width ?? SIZE_WIDTH[size];

  return (
    <BaseModal
      key={formId}
      aria-labelledby="unstyled-modal-title"
      aria-describedby="unstyled-modal-description"
      open={isOpen}
      // `disableDismiss` no-ops the MUI Modal's onClose so backdrop clicks
      // and the ESC key are ignored — the action button is the only exit.
      onClose={disableDismiss ? undefined : closeModal}
      slots={{ backdrop: Backdrop }}
      className={styles.modal}
      onTransitionExited={onTransitionExited}
    >
      <div
        className={clsx(
          styles.modalWrapper,
          forceWhiteBackground && styles.modalWrapperWhiteBackground,
        )}
        style={{
          width: computedWidth,
          height,
        }}
      >
        <div className={styles.modalHeader}>
          {modalLabel}
          {!disableDismiss && (
            <Button noHover onClick={closeModal}>
              <Icon
                iconName="PlainCloseIcon"
                width="1.25rem"
                height="1.25rem"
              />
            </Button>
          )}
        </div>
        <div className={styles.modalContent}>{children}</div>
        {buttonLabel && (
          <div className={styles.modalButtonsWrapper}>
            <div className={styles.modalButtonsLeft}>{footerLeft}</div>
            <div className={styles.modalButtonsRight}>
              {!hideCancel && (
                <Button
                  outlined
                  onClick={closeModal}
                  disabled={isLoading || isLoadingOptionalButton}
                >
                  {labels.CANCEL}
                </Button>
              )}
              {optionalButtonLabel && (
                <Button
                  onClick={() => optionalButtonAction?.()}
                  withSpinner={isLoadingOptionalButton}
                  disabled={isLoading}
                >
                  {optionalButtonLabel}
                </Button>
              )}
              <Button
                withSpinner={isLoading}
                type="submit"
                formId={formId}
                disabled={isDisabled || isLoading}
                danger={actionButtonColor === "danger"}
                onClick={action}
              >
                {buttonLabel}
              </Button>
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
};

export default Modal;
